import React, { useCallback, useEffect, useRef, useState } from "react";
import Card from "../components/Card";
import { useSelector } from "react-redux";
import { getSelectedTimesheetDateRange } from "store/selector/rostering.selector";
import Loader from "components/common/Loader/Loader";
import TimesheetSearchField from "../components/TimesheetRevamp/TimesheetSearchField";
import { TimesheetWorkerListSkeleton } from "../components/TimesheetRevamp/TimesheetPageSkeleton";
import "../components/TimesheetRevamp/timesheet-detail-revamp.css";
import { isBoolean } from "lodash";
import { WORKER_SIDEBAR_LIMIT } from "Constants/constant";
import { HiOutlineArrowsUpDown } from "react-icons/hi2";
import {
  participantFilterKey,
  resolveTimesheetPtIdFilter,
} from "../utils/timesheetParticipantFilter";

const SCROLL_LOAD_THRESHOLD_PX = 80;

/** Survives sidebar remount so status-filter refetch does not reload workers. */
const sidebarFetchState = {
  activeFilterKey: "",
  pendingFilterKey: "",
};

export const resetSidebarFetchState = () => {
  sidebarFetchState.activeFilterKey = "";
  sidebarFetchState.pendingFilterKey = "";
};

const buildFilterKey = ({
  selectedParticipants,
  fromDate,
  toDate,
  search,
  sortField = "first_name",
  sortOrder = "asc",
}) => {
  const participantKey = participantFilterKey(selectedParticipants);
  const rangeKey =
    fromDate && toDate ? `${fromDate}-${toDate}` : "";
  const searchKey = (search || "").trim();
  return `${participantKey}|${rangeKey}|${searchKey}|${sortField}|${sortOrder}`;
};

const filterKeyFromQueryParams = (queryParams) => {
  if (!queryParams) return "";
  let ptId = queryParams.pt_id;
  if (Array.isArray(ptId)) {
    // Must match participantFilterKey(): sorted ids so multi-select responses apply.
    ptId =
      ptId.length > 0
        ? [...ptId]
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id))
            .sort((a, b) => a - b)
            .join(",")
        : "all";
  } else if (ptId == null || ptId === "" || ptId === "all") {
    ptId = "all";
  } else {
    ptId = String(ptId);
  }

  let rangeKey = "";
  const range = queryParams.timesheet_date_range;
  if (Array.isArray(range) && range.length >= 2) {
    rangeKey = `${range[0]}-${range[1]}`;
  } else if (typeof range === "string" && range !== "all") {
    rangeKey = range;
  }

  const searchKey = (queryParams.search || "").trim();
  const sortField = queryParams.sort_field || "first_name";
  const sortOrder = queryParams.sort_order || "asc";
  return `${ptId}|${rangeKey}|${searchKey}|${sortField}|${sortOrder}`;
};

const TimeSheetSidebar = ({
  getTimesheetsofWorkers,
  timeSheetsListOfWorkers,
  setSelectedWorkerInSidebar,
  selectedWorkerInSidebar,
  spRosteringSettingLoader,
  setPage,
  setCheckboxes,
  workersLoader,
  selectedParticipants,
  onSidebarLoadedCountChange,
  onWorkersChange,
}) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [accumulatedWorkers, setAccumulatedWorkers] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadedPage, setLoadedPage] = useState(0);

  const scrollContainerRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const activeFilterKeyRef = useRef("");
  const pendingFilterKeyRef = useRef("");
  const prevReportedCountRef = useRef(0);
  const userHasScrolledRef = useRef(false);
  const getTimesheetsofWorkersRef = useRef(getTimesheetsofWorkers);
  const buildWorkerParamsRef = useRef(null);
  const dateRange = useSelector(getSelectedTimesheetDateRange);

  useEffect(() => {
    activeFilterKeyRef.current = "";
    pendingFilterKeyRef.current = "";
    resetSidebarFetchState();
  }, []);

  useEffect(() => {
    getTimesheetsofWorkersRef.current = getTimesheetsofWorkers;
  }, [getTimesheetsofWorkers]);

  const normalizedSearch = (debouncedSearch || "").trim();

  const filterKey = buildFilterKey({
    selectedParticipants,
    fromDate: dateRange?.fromDate,
    toDate: dateRange?.toDate,
    search: normalizedSearch,
    sortField: "first_name",
    sortOrder,
  });

  const resolvePtId = useCallback(
    () => resolveTimesheetPtIdFilter(selectedParticipants),
    [selectedParticipants]
  );

  const buildWorkerParams = useCallback(
    (page) => {
      if (!dateRange?.fromDate || !dateRange?.toDate) {
        return null;
      }
      return {
        page,
        per_page: WORKER_SIDEBAR_LIMIT,
        timesheet_date_range: [dateRange.fromDate, dateRange.toDate],
        pt_id: resolvePtId(),
        search: normalizedSearch || null,
        sort_field: "first_name",
        sort_order: sortOrder,
      };
    },
    [
      dateRange?.fromDate,
      dateRange?.toDate,
      normalizedSearch,
      resolvePtId,
      sortOrder,
    ]
  );

  useEffect(() => {
    buildWorkerParamsRef.current = buildWorkerParams;
  }, [buildWorkerParams]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  const prevSearchRef = useRef(normalizedSearch);

  useEffect(() => {
    if (prevSearchRef.current !== normalizedSearch) {
      prevSearchRef.current = normalizedSearch;
      setSelectedWorkerInSidebar(null);
      setPage(1);
      setCheckboxes([]);
    }
  }, [
    normalizedSearch,
    setCheckboxes,
    setPage,
    setSelectedWorkerInSidebar,
  ]);

  useEffect(() => {
    if (!workersLoader) {
      loadingMoreRef.current = false;
    }
  }, [workersLoader]);

  useEffect(() => {
    const count = accumulatedWorkers.length;
    if (prevReportedCountRef.current !== count) {
      prevReportedCountRef.current = count;
      onSidebarLoadedCountChange?.(count);
    }
    onWorkersChange?.(accumulatedWorkers);
  }, [accumulatedWorkers, onSidebarLoadedCountChange, onWorkersChange]);

  useEffect(() => {
    if (workersLoader || !timeSheetsListOfWorkers?.workers?.data) {
      return;
    }

    const queryParams = timeSheetsListOfWorkers?.query_params;
    const responseFilterKey = filterKeyFromQueryParams(queryParams);
    const matchesPending = responseFilterKey === pendingFilterKeyRef.current;
    const matchesActive = responseFilterKey === activeFilterKeyRef.current;
    if (!matchesPending && !matchesActive) {
      return;
    }

    activeFilterKeyRef.current = responseFilterKey;
    pendingFilterKeyRef.current = "";
    sidebarFetchState.activeFilterKey = responseFilterKey;
    sidebarFetchState.pendingFilterKey = "";

    const { data, current_page, total_pages, total, per_page } =
      timeSheetsListOfWorkers.workers;
    const reqPage = queryParams?.page ?? current_page ?? 1;
    const reqPerPage =
      queryParams?.per_page ?? per_page ?? WORKER_SIDEBAR_LIMIT;

    if (reqPage <= 1 && reqPerPage > WORKER_SIDEBAR_LIMIT) {
      setAccumulatedWorkers(data);
      setLoadedPage(Math.max(1, Math.ceil(data.length / WORKER_SIDEBAR_LIMIT)));
      setHasMore(data.length < (total ?? 0));
      loadingMoreRef.current = false;
      return;
    }

    if (reqPage <= 1) {
      setAccumulatedWorkers(data);
    } else {
      setAccumulatedWorkers((prev) => {
        const ids = new Set(prev.map((w) => w.id));
        return [...prev, ...data.filter((w) => !ids.has(w.id))];
      });
    }

    setLoadedPage(current_page);
    setHasMore(current_page < total_pages);
    loadingMoreRef.current = false;
  }, [timeSheetsListOfWorkers, workersLoader]);

  useEffect(() => {
    if (!dateRange?.fromDate || !dateRange?.toDate) return;

    const paramsBuilder = buildWorkerParamsRef.current;
    if (!paramsBuilder) return;

    const params = paramsBuilder(1);
    if (!params) return;

    // One in-flight request per filter key (takeLatest cancels stale requests in saga).
    if (activeFilterKeyRef.current === filterKey) {
      return;
    }
    if (pendingFilterKeyRef.current === filterKey) {
      return;
    }

    pendingFilterKeyRef.current = filterKey;
    sidebarFetchState.pendingFilterKey = filterKey;
    userHasScrolledRef.current = false;

    setAccumulatedWorkers([]);
    setHasMore(true);
    setLoadedPage(0);
    loadingMoreRef.current = false;
    prevReportedCountRef.current = 0;

    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "auto" });

    getTimesheetsofWorkersRef.current(params);
  }, [filterKey, dateRange?.fromDate, dateRange?.toDate]);

  const loadMore = useCallback(() => {
    if (
      loadingMoreRef.current ||
      workersLoader ||
      !hasMore ||
      loadedPage < 1 ||
      accumulatedWorkers.length === 0 ||
      !userHasScrolledRef.current
    ) {
      return;
    }

    loadingMoreRef.current = true;
    const paramsBuilder = buildWorkerParamsRef.current;
    const params = paramsBuilder?.(loadedPage + 1);
    if (!params) {
      loadingMoreRef.current = false;
      return;
    }
    getTimesheetsofWorkersRef.current(params);
  }, [accumulatedWorkers.length, hasMore, loadedPage, workersLoader]);

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el || workersLoader || loadingMoreRef.current) return;

    if (el.scrollTop > 0) {
      userHasScrolledRef.current = true;
    }

    if (!userHasScrolledRef.current) return;

    const distanceFromBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom <= SCROLL_LOAD_THRESHOLD_PX) {
      loadMore();
    }
  }, [loadMore, workersLoader]);

  const handleToggleSort = useCallback(() => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    setSelectedWorkerInSidebar(null);
    setPage(1);
    setCheckboxes([]);
  }, [setCheckboxes, setPage, setSelectedWorkerInSidebar]);

  const sortLabel =
    sortOrder === "asc" ? "Name A–Z" : "Name Z–A";

  const isInitialLoading =
    (workersLoader || spRosteringSettingLoader) &&
    accumulatedWorkers.length === 0;
  const isLoadingMore = workersLoader && accumulatedWorkers.length > 0;

  const workerTotal =
    timeSheetsListOfWorkers?.workers?.total ?? accumulatedWorkers.length;

  return (
    <div className="flex h-full max-h-full min-h-0 flex-col overflow-hidden">
      <div className="flex items-center justify-between border-b border-[#E5E7EB] px-4 py-3.5">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-[#0F172A]">Workers</span>
          <span
            className="rounded-full bg-[#EEF2FB] px-2 py-0.5 text-[11px] font-bold text-[#4A6BB6]"
          >
            {workerTotal}
          </span>
        </div>
        <button
          type="button"
          onClick={handleToggleSort}
          disabled={isInitialLoading}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-transparent text-[#64748B] transition-colors hover:border-[#E5E7EB] hover:bg-[#F8FAFC] hover:text-[#4A6BB6] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4A6BB6]/30 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label={`Sort workers: ${sortLabel}. Click to reverse.`}
          title={sortLabel}
        >
          <HiOutlineArrowsUpDown
            className={`h-4 w-4 ${sortOrder === "desc" ? "text-[#4A6BB6]" : ""}`}
            aria-hidden
          />
        </button>
      </div>

      <div className="border-b border-[#E5E7EB] p-3">
        <TimesheetSearchField
          value={search}
          onChange={setSearch}
          placeholder="Search workers…"
        />
      </div>

      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="timesheet-panel-scroll flex-1 space-y-2 overflow-y-auto p-3 min-h-0"
      >
        {isInitialLoading ? (
          <TimesheetWorkerListSkeleton />
        ) : accumulatedWorkers.length > 0 ? (
          <>
            {accumulatedWorkers.map((singleTimesheet) => (
              <Card
                key={singleTimesheet.id}
                singleTimesheet={singleTimesheet}
                selectedWorkerInSidebar={selectedWorkerInSidebar}
                selectedWorker={
                  selectedWorkerInSidebar?.id != null &&
                  singleTimesheet?.id != null &&
                  String(selectedWorkerInSidebar.id) ===
                    String(singleTimesheet.id)
                }
                setSelectedWorkerInSidebar={setSelectedWorkerInSidebar}
                setPage={setPage}
                isSmallAvatart
                isInactive={
                  (isBoolean(singleTimesheet?.status) &&
                    singleTimesheet?.status === false) ||
                  String(singleTimesheet?.status).toLowerCase() === "inactive"
                }
              />
            ))}
            {isLoadingMore && (
              <div className="flex items-center justify-center py-2">
                <Loader cssClass="text-[#4A6BB6] h-6 w-6" />
              </div>
            )}
          </>
        ) : (
          <div className="flex h-32 items-center justify-center">
            <p className="text-xs text-[#94A3B8]">No workers found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeSheetSidebar;
